/** Enrollment pins are created only by the local, user-approved passkey ceremony. */
import {
  approvalChallengeFor,
  createEnrollmentChallenge,
  credentialSetHash,
  DOMAIN,
  type EcPublicJwk,
  type EnrollmentApproval,
  jwkThumbprint,
  type SignedRevocationReport,
  verifyRevocationReport,
  verifyStatement,
} from '@viko/crypto'
import { CONTENT_IDENTITY_DOMAIN, DaemonContentIdentityProof } from '@viko/protocol'
import { loadContentTrust } from './content-trust.js'
import { idb } from './idb.js'

const pinKey = (accountId: string, machineId: string) =>
  `content-daemon-pin/v1/${JSON.stringify([accountId, machineId])}`
interface Pin {
  daemonKeyId: string
  publicKey: EcPublicJwk
}
export async function pinApprovedContentDaemon(accountId: string, approval: EnrollmentApproval) {
  const local = structuredClone(approval)
  const trust = await loadContentTrust(accountId)
  const s = local.statement
  if (s.credentialSetHash !== (await credentialSetHash(trust.credentials())))
    throw new Error('Enrollment credential set needs a locally approved transfer')
  if (
    s.rpId !== trust.rp.rpId ||
    s.expiresAt < Date.now() ||
    s.issuedAt > Date.now() + 60_000 ||
    s.daemonKeyId !== (await jwkThumbprint(s.daemonPublicKey))
  )
    throw new Error('Invalid local enrollment approval')
  // One tap may approve the enrollment together with the genesis directory (approval batch).
  const challenge = await approvalChallengeFor(
    DOMAIN.enrollment,
    await createEnrollmentChallenge(s),
    local.batch,
  )
  if (!(await trust.verifyPasskeyApproval(local.assertion, challenge)).ok)
    throw new Error('Enrollment requires a locally trusted passkey')
  const pin = { daemonKeyId: s.daemonKeyId, publicKey: s.daemonPublicKey }
  await idb.update<Pin>(pinKey(accountId, s.machineId), (current) => {
    if (current && current.daemonKeyId !== pin.daemonKeyId)
      throw new Error('Machine identity changed; explicit recovery required')
    return pin
  })
}
/** passkey-budget WP1: this browser's own enrollment pin for a machine (never relay-supplied). */
export async function loadContentDaemonPin(
  accountId: string,
  machineId: string,
): Promise<Pin | undefined> {
  return (await idb.get<Pin>(pinKey(accountId, machineId)).catch(() => undefined)) ?? undefined
}
/** Did this browser approve (and pin) this machine's enrollment? */
export async function hasApprovedContentDaemon(accountId: string, machineId: string) {
  return !!(await idb.get<Pin>(pinKey(accountId, machineId)))
}
export async function verifiedDaemonContentKey(accountId: string, machineId: string, raw: unknown) {
  const proof = DaemonContentIdentityProof.parse(raw)
  const pin = await idb.get<Pin>(pinKey(accountId, machineId))
  if (!pin) throw new Error('Approve this machine locally before sharing encrypted content')
  const s = proof.statement
  if (
    s.accountId !== accountId ||
    s.machineId !== machineId ||
    s.daemonKeyId !== pin.daemonKeyId ||
    !(await verifyStatement(pin.publicKey, CONTENT_IDENTITY_DOMAIN, s, proof.signature))
  )
    throw new Error('Untrusted machine content key')
  return s.publicKey
}

// ---- f-keys: signed daemon revocation-sequence reports ----
/** Verify a relayed report with the locally approved enrollment pin, never a relay-supplied key. */
export async function verifiedRevocationReport(
  accountId: string,
  machineId: string,
  signed: SignedRevocationReport,
) {
  const pin = await idb.get<Pin>(pinKey(accountId, machineId))
  if (!pin) return undefined
  return verifyRevocationReport(signed, pin.publicKey, { accountId, machineId })
}
